// client/src/hooks/useContactMessages.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const CONTACT_KEY = 'contactMessages';

/**
 * Caixa de Mensagens de contato (formulário de contato da home — ver
 * HomeConfig seção 'contact'). Isolamento por tenant (matriz/franquia/conta)
 * é feito no backend.
 */
export const useContactMessages = (params = {}) => {
    return useQuery({
        queryKey: [CONTACT_KEY, 'lista', params],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/institucional/contact', { params });
            return {
                data: data.data || [],
                count: data.count || 0,
                totalCount: data.totalCount || 0,
                page: data.page || 1,
                totalPages: data.totalPages || 1,
            };
        },
        staleTime: 1000 * 30,
    });
};

export const useContactMessage = (id) => {
    return useQuery({
        queryKey: [CONTACT_KEY, 'detalhe', id],
        queryFn: async () => {
            const { data } = await apiClient.get(`/admin/institucional/contact/${id}`);
            return data.data;
        },
        enabled: !!id,
    });
};

export const useMutateContactMessages = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [CONTACT_KEY] });
    };

    const updateStatus = useMutation({
        mutationFn: ({ id, status }) => apiClient.put(`/admin/institucional/contact/${id}`, { status }),
        onSuccess: () => { invalidate(); setAlert('Status atualizado com sucesso!', 'success'); },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao atualizar status.', 'danger'),
    });

    const remove = useMutation({
        mutationFn: (id) => apiClient.delete(`/admin/institucional/contact/${id}`),
        onSuccess: () => { invalidate(); setAlert('Mensagem excluída com sucesso!', 'success'); },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao excluir mensagem.', 'danger'),
    });

    const reply = useMutation({
        mutationFn: ({ id, replyMessage }) => apiClient.post(`/admin/institucional/contact/${id}/reply`, { replyMessage }),
        onSuccess: (response) => {
            invalidate();
            // ✅ A resposta é sempre salva, mas o e-mail em si só sai se a
            // conta/franquia/matriz tiver uma credencial de e-mail configurada
            // — emailEnviado reflete isso de verdade, em vez de mostrar
            // "enviado com sucesso" mesmo quando nada foi entregue.
            const emailEnviado = response.data?.emailEnviado;
            setAlert(response.data?.message || 'Resposta registrada.', emailEnviado ? 'success' : 'warning');
        },
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao enviar resposta.', 'danger'),
    });

    return { updateStatus, remove, reply };
};
